import React, { useState, useEffect } from 'react';
import {
    Text,
    makeStyles,
    Spinner,
} from '@fluentui/react-components';
import { useTokenService } from '../../tokenService';
import { CopilotService } from '../../Service/ServerAPI';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/store';
import JsonViewer from './JsonViewer';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        height: '100%',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px',
        gap: '12px',
    },
    responseSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
});

const DebugSchema: React.FC<{ dataset?: string }> = ({ dataset }) => {
    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(true);
    const [response, setResponse] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);

    const getAccessToken = useTokenService();
    const copilotService = CopilotService.getInstance(getAccessToken);
    const settings = useSelector((state: RootState) => state.settings);

    useEffect(() => {
        const fetchCombinedSchemas = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const result = await copilotService.getCombinedSchemas(
                    settings.apiEndpoint,
                    dataset
                );

                if (result.error) {
                    setError(result.error);
                    setResponse(null);
                } else {
                    setResponse(result);
                    setError(null);
                }
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : 'An unexpected error occurred';
                setError(errorMessage);
                setResponse(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCombinedSchemas();
    }, [dataset]); // Re-fetch when dataset changes

    if (isLoading) {
        return (
            <div className={classes.container}>
                <div className={classes.loadingContainer}>
                    <Spinner size="medium" />
                    <Text>Loading combined schemas...</Text>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <Text size={500} weight="semibold">Combined User Document Schemas</Text>
                <Text size={300}>
                    This shows the combined schema structure of user documents in the database.
                </Text>
            </div>

            <div className={classes.responseSection}>
                <JsonViewer data={response} error={error} />
            </div>
        </div>
    );
};

export default DebugSchema;